import { selectCurrentHotelId, selectLoggedinUserEmail } from 'accessors'
import Dialog from 'components/ReusableComponents/Dialog'
import BankIcon from 'components/ReusableComponents/Icons/Bank'
import CreditCardIcon from 'components/ReusableComponents/Icons/CreditCard'
import SendIcon from 'components/ReusableComponents/Icons/Send'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import ACHContractDialog from 'features/ACHContract'
import MakePayment from 'features/Billing/MakePayment'
import CheckACHStatus from 'features/HotelSetup/CheckACHStatus'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useRouteMatch } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import hotelSetupApi from 'services/api/hotelSetup'
import subsidiariesApi from 'services/api/subsidiaries'
import { getOrdinalNum } from 'utils/getOrdinalNum'

import featuresImg from './features.png'
import styles from './HotelSetupFinalize.module.scss'

const HotelSetupFinalize = () => {
  const paymentDialogMatch = useRouteMatch('/hotel-setup/payment')
  const ACHContractSignedDialogMatch = useRouteMatch('/hotel-setup/ach-contract-signed')

  const [isAutoPay, setIsAutoPay] = useState()
  const [activePeriod, setActivePeriod] = useState(false)
  const [whenToCharge, setWhenToCharge] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [achFormUrl, setAchFormUrl] = useState()
  const [isAchFormVisible, setIsAchFormVisible] = useState(false)

  const [accountPayableUsers, setAccountPayableUsers] = useState()

  const loggedinUserEmail = useSelector(selectLoggedinUserEmail)

  const currentSubsidiaryId = useSelector(selectCurrentHotelId)

  const { addToast } = useToasts()

  function shareWithAccountPayable() {
    const accountPayableUser = accountPayableUsers[0]

    subsidiariesApi
      .getHotelSetupLoginLink({
        userId: accountPayableUser.userId,
        isSendEmail: true,
        subsidiaryId: currentSubsidiaryId
      })
      .then(() => {
        addToast(`Link has been shared with ${accountPayableUser.email}`, {
          appearance: 'success',
          autoDismiss: true
        })
      })
      .catch(() => {
        addToast(`Something went wrong while sharing the link`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
  }

  useEffect(() => {
    setIsLoading(true)

    hotelSetupApi
      .finalDetails()
      .then(res => {
        setIsAutoPay(res.isAutoPayEnabled)
        setAccountPayableUsers(
          res.accountPayableUsers.filter(u => u.email !== loggedinUserEmail)
        )
        setActivePeriod(res.activePeriod)
        setWhenToCharge(res.whenToCharge)
        setIsLoading(false)
      })
      .catch(() => {
        addToast('Error loading data', { appearance: 'error' })
      })
  }, [addToast, loggedinUserEmail])

  function getAchForm() {
    setIsLoading(true)

    hotelSetupApi
      .getAchAuthForm()
      .then(res => {
        setAchFormUrl(res)
        setIsAchFormVisible(true)
      })
      .catch(() => {
        addToast('Something went wrong while getting ACH auth form', {
          appearance: 'error'
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (ACHContractSignedDialogMatch) {
      setIsAchFormVisible(false)
    }
  }, [ACHContractSignedDialogMatch])

  const autoPayText = useMemo(() => {
    if (activePeriod === 'Monthly') {
      return `${getOrdinalNum(whenToCharge)} of each month`
    } else if (activePeriod === 'Quarterly') {
      return `${getOrdinalNum(whenToCharge)} day of every third month`
    } else if (activePeriod === 'Annual') {
      return `${getOrdinalNum(whenToCharge)} day of 12 months`
    }
  }, [activePeriod, whenToCharge])

  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <div className={styles.container}>
      {isAchFormVisible && (
        <ACHContractDialog url={achFormUrl} onClose={() => setIsAchFormVisible(false)} />
      )}
      <div className={styles.mainCard}>
        <div className={styles.mainCardHeader}>
          <img className={styles.mainCardHeaderImg} src={featuresImg} alt="features" />
        </div>
        <div className={styles.mainCardContent}>
          <p className={styles.headingTag}>Get started with Directful</p>
          <h1 className={styles.heading}>
            You're one step away from unlocking more revenue.
          </h1>
          <p className={styles.paragraph}>
            Take the final step to boost your conversions and revenue. Activate your
            Directful subscription today and get instant access to Directful.
          </p>
          <p className={styles.paragraph}>
            Choose ACH or Credit Card Payment below to get started.
          </p>
          {paymentDialogMatch && (
            <Dialog maxWidth={800}>
              <div className={styles.dialogInner}>
                <MakePayment
                  goBackPath={'/hotel-setup'}
                  showTabs={false}
                  isAmountLocked={true}
                  isAutoPay={isAutoPay}
                />
              </div>
            </Dialog>
          )}
          {ACHContractSignedDialogMatch && (
            <Dialog maxWidth={800}>
              <CheckACHStatus />
            </Dialog>
          )}
          <div className={styles.ctas}>
            {isAutoPay ? (
              <button className={styles.cta} onClick={getAchForm}>
                <BankIcon />
                <span className={styles.ctaText}>Pay with ACH</span>
              </button>
            ) : (
              <Link className={styles.cta} to="/hotel-setup/payment/check">
                <BankIcon />
                <span className={styles.ctaText}>Pay with ACH</span>
              </Link>
            )}
            <Link to="/hotel-setup/payment/credit-card" className={styles.cta}>
              <CreditCardIcon />
              <span className={styles.ctaText}>Pay with credit card</span>
            </Link>
          </div>
          {accountPayableUsers.length > 0 && (
            <div className={styles.accountPayableShareHolder}>
              <button
                onClick={shareWithAccountPayable}
                className={styles.accountPayableShareHolderButton}
              >
                <SendIcon /> Share this page with your account payable,{' '}
                {accountPayableUsers[0].name}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={styles.sidebar}>
        {isAutoPay && (
          <div className={styles.sidebarSection}>
            <h2 className={styles.sidebarSectionHeading}>Automated payments</h2>
            <p className={styles.sidebarParagraph}>
              Your account has been configured for automated payments, and charges will be
              processed on the {autoPayText} including your amount due, plus or minus any
              payment related activities or adjustments.
            </p>
          </div>
        )}
        <div className={styles.sidebarSection}>
          <h2 className={styles.sidebarSectionHeading}>What is ACH?</h2>
          <p className={styles.sidebarParagraph}>
            ACH, or Automated Clearing House, is an electronic network used for
            transferring funds between banks and financial institutions in the U.S. It
            facilitates direct deposits, bill payments, and other transactions, offering a
            fast and secure method for moving money.
          </p>
        </div>

        {!isAutoPay && (
          <div className={styles.sidebarSection}>
            <h2 className={styles.sidebarSectionHeading}>First-class mail</h2>
            <p className={styles.sidebarParagraph}>
              For those who prefer traditional methods, you can send your payment to: 4101
              Dublin Blvd, Ste F #3007, Dublin, CA 94568
              <br /> Please note: Paying by mail can be slower, and any delays might
              result in disruptions to your service. Ensure your payment is sent well in
              advance to avoid any potential service gaps.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default HotelSetupFinalize
